import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { ALL_STYLE_CATEGORY_IDS, applyStyle, captureStyle } from 'modules/manager/domain/style-clipboard';
import { useEffect, useRef } from 'react';
import { useStyleClipboard } from './use-style-clipboard';

const COPY_COMBOS = ['ctrl+shift+c', 'meta+shift+c'];
const PASTE_COMBOS = ['ctrl+shift+v', 'meta+shift+v'];

/**
 * Ctrl/⌘+Shift+C copies the style of a single selected object into the style
 * clipboard; Ctrl/⌘+Shift+V pastes every captured category (size included)
 * onto the selection. Must be called under a StyleClipboardProvider.
 */
export function useStyleClipboardHotkeys(editor: FabricEditor | null): void {
  const { captured, setCaptured } = useStyleClipboard();

  const selectionRef = useRef<fabric.Object[]>([]);
  const capturedRef = useRef(captured);
  capturedRef.current = captured;

  useEffect(() => {
    if (!editor) return;
    selectionRef.current = editor.selection?.getActive() ?? [];
    const off = editor.on('selection:changed', (e) => {
      selectionRef.current = e.active;
    });
    return off;
  }, [editor]);

  useEffect(() => {
    const hotkeys = editor?.hotkeys;
    if (!editor || !hotkeys) return;

    const copy = () => {
      const selection = selectionRef.current;
      if (selection.length !== 1) return;
      setCaptured(captureStyle(selection[0], editor));
    };
    const paste = () => {
      const selection = selectionRef.current;
      const clipboard = capturedRef.current;
      if (!clipboard || selection.length === 0) return;
      applyStyle(editor, selection, clipboard, ALL_STYLE_CATEGORY_IDS);
    };

    COPY_COMBOS.forEach((combo) => hotkeys.bind(combo, copy));
    PASTE_COMBOS.forEach((combo) => hotkeys.bind(combo, paste));
    return () => {
      COPY_COMBOS.forEach((combo) => hotkeys.unbind(combo));
      PASTE_COMBOS.forEach((combo) => hotkeys.unbind(combo));
    };
  }, [editor, setCaptured]);
}
